
import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import AdvancedFormat from "dayjs/plugin/advancedFormat";
import Duration from "dayjs/plugin/duration";
import IsoWeek from "dayjs/plugin/isoWeek";
import IsSameOrBefore from "dayjs/plugin/isSameOrBefore";
import IsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import UTC from "dayjs/plugin/utc";
import QuarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(CustomParseFormat);
dayjs.extend(AdvancedFormat);
dayjs.extend(Duration);
dayjs.extend(IsoWeek);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsSameOrAfter);
dayjs.extend(UTC);
dayjs.extend(QuarterOfYear);

import { DateRange, HolidayDate, HolidayGroup, Holidays } from "../model";
//import { Objects } from "./Objects";
import { DatelyObject } from "./DatelyObject";
import { yearlyHolidaysYears } from "../codegen/yearlyHolidaysYears";

/**
 * Date parsing, formatting and other operations within the Salaxy framework.
 * We use ISO 8601 as our Date format even inside JavaScript objects.
 * This is because of the following reasons:
 *
 * 1. Generally, we do not want to use JavaScript Date because it presents so many problems.
 * 2. We store dates in JSON as ISO 8601 date strings - this is universally the best practice
 * 3. ISO 8601 provides a way to present a date without time and most importantly timezone information
 * 4. ISO 8601 date also works best for most UI components (because of similar reasons as above).
 *
 * This library is only for Dates. Time / DateTime functionality is in DateTimes util library.
 *
 * Implementation uses Day.js
 */
export class Dates {


  /**
   * Gets an object and returns it as the date format that is stored in the data structure with ISO 8601 format.
   *
   * @param datelyObject - Object that should be converted to Salaxy date: A string, JS Date object, Day.js etc.
   *
   * @returns The date in simplest possible ISO 8601 compliant format "YYYY-MM-DD", undefined if empty, not valid date etc.
   */

  public static asDate(datelyObject: DatelyObject | any): string | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? dayJs.format("YYYY-MM-DD") : undefined;
  }

  /**
   * Gets a date based on year, month and day (1-based).
   * NOTE: Unlike Date() constructor or dayjs() constructor, all numbers are 1-based:
   * (2017, 2, 2) results to "2017-02-02" and not "2017-01-01T22:00:00.000Z" like in those constructors.
   *
   * @param year Year component of the date or "today" for today's year.
   * @param month Month component of the date (1-12) or "today" for today's month.
   * @param day Day of month component of the date (1-31) or "today" for today's day of month.
   */
  public static getDate(year: number | "today", month: number | "today", day: number | "today"): string | undefined {
    year = (year === "today") ? Dates.getDayJs("today").year() as number : year;
    month = (month === "today") ? Dates.getDayJs("today").month() as number : (month - 1);
    day = (day === "today") ? Dates.getDayJs("today").date() : day;
    if (!day || month == null || month < 0 || month > 11 || !year) {
      return undefined;
    }
    return this.asDate([year, month, day]);
  }

  /**
   * Constructs a DatelyObject (DayJs) from any DayJs compatible object
   * @param timestamp any DayJs compatible object to construct a DatelyObject.
   * @returns DatelyObject
   */
    public static getDatelyObject(timestamp: any) : DatelyObject | undefined {
      return Dates.getDayJs(timestamp);
    }

  /**
   * Converts the date to a JavaScript Date. Please note, that also time part is returned.
   * @param  datelyObject - Object that should be converted: A string, JS Date object, DayJs etc.
   *
   * @returns JavaScript date corresponding to input or null.
   */
  public static asJSDate(datelyObject: DatelyObject | null | undefined): Date | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? dayJs.toDate() : undefined;
  }


  /**
   * Checks whether a string is in ISO format.
   *
   * @param dateProspect Value that is evaluated.
   * @param required If set to true, the value is required. If required is null the false will be returned.
   * @param format Date or datetime format. Default is ISO_8601 (ISO datetime). TODO other supported formats.
   *
   * @returns True if the given date is in the correct format.
   */
  public static isValidDateTime(dateProspect: string, required = false, format : string | undefined = undefined): boolean {
    if (!dateProspect) {
      return !required;
    }
    let djs: dayjs.Dayjs;
    if (format) {
      djs = dayjs(dateProspect, format, true);
    }
    else {
      djs = dayjs(dateProspect);
    }

    if (djs) {
      return djs.isValid();
    }
    return false;
  }

  /**
   * Parses a date. In ISO format by default.
   *
   * @param dateProspect Value that is parsed.
   * @param format Date or datetime format. Default is ISO_8601 (ISO datetime).
   * Note that result is just a date (not datetime).
   *
   * @returns True if the given date is in the correct format.
   */
  public static parseDate(dateProspect: string, format: string | undefined = undefined): string | undefined {
    if (!dateProspect) {
      return undefined;
    } else {
      let djs: dayjs.Dayjs;
      if (format) {
        djs = dayjs(dateProspect, format, true);
      }
      else {
        djs = dayjs(dateProspect);
      }
      return Dates.asDate(djs);
    }
  }

  /** Get the date of today as ISO 8601 string format (YYYY-MM-DD)  */
  public static getToday(): string {
    return this.asDate(dayjs())!;
  }




  /**
   * Returns Day.Js -object.
   *
   * @param datelyObject Object that should be converted to Day.js -object: A string, JS Date object, Day.js, Number[3] etc.
   * @param allowInvalid If true and datelyObject is faulty string, returns invalid Day.js -object object. By default returns undefined, if parameter cannot be parsed.
   *
   * @returns A Day.js -object or undefined.
   */
  public static getDayJs(datelyObject: DatelyObject | null | undefined, allowInvalid = false): any | undefined {
    if (!datelyObject) {
      return undefined;
    }

    let djs: dayjs.Dayjs;
    if (datelyObject instanceof Date){
      djs = dayjs(datelyObject);
    } else if (Number.isInteger(datelyObject as number)) {
      djs = dayjs(datelyObject as number);
    } else if (Array.isArray(datelyObject)) {
      let d: Date;
      switch (datelyObject.length) {
        case 3: // YYYY-MM-DD
          d = new Date(datelyObject[0],datelyObject[1],datelyObject[2]);
          break;
        case 5: // YYYY-MM-DD HH:mm
          d = new Date(datelyObject[0],datelyObject[1],datelyObject[2],datelyObject[3],datelyObject[4]);
          break;
        case 6: // YYYY-MM-DD HH:mm:ss
          d = new Date(datelyObject[0],datelyObject[1],datelyObject[2],datelyObject[3],datelyObject[4],datelyObject[5]);
          break;
        default:
          return undefined;
      }
      djs = dayjs(d);
    } else if (datelyObject === "today") {
      djs = dayjs(new Date());
      djs = djs.startOf("day");
    } else if (datelyObject === "yesterday") {
      djs = dayjs(new Date());
      djs = djs.startOf("day");
      djs = djs.add(-1, "day");
    } else if (datelyObject === "tomorrow") {
      djs = dayjs(new Date());
      djs = djs.startOf("day");
      djs = djs.add(1, "day");
    }
    else {
      djs = dayjs(datelyObject);
    }

    if (djs.isValid() || allowInvalid) {
      return djs;
    }
    return undefined;
  }


  /**
   * Returns the duration  object. If parameter is empty or null, defaults to Today (not Now).
   *
   * @param startDatelyObject Duration start that is parsed using getDayJs().
   * Null before conversion defaults to today. Invalid objects result to invalid duration.
   * @param endDatelyObject Duration start that is parsed using getDayJs().
   * Null before conversion defaults to today. Invalid objects result to invalid duration.
   *
   * @returns A Duration object, which may also be invalid
   */
  public static getDuration(startDatelyObject: DatelyObject, endDatelyObject: DatelyObject): any {
    const start = this.getDayJs(startDatelyObject, true) || this.getDayJs("today");
    const end = this.getDayJs(endDatelyObject, true)|| this.getDayJs("today");

    const d = dayjs.duration(end.diff(start));
    return d;
  }

  /**
   * Gets a date range based on start and end dates.
   * Days count is calculated using getWorkdays() (Add parameter if you need other day count methods).
   *
   * @param start DateRange start, DatelyObject that should be converted to Salaxy date: A string, JS Date object, Day.js  etc.
   * @param end DateRange end, DatelyObject that should be converted to Salaxy date: A string, JS Date object, Day.js etc.
   *
   * @returns DateRange based on start and end dates.
   */
  public static getDateRange(start: DatelyObject, end: DatelyObject): DateRange {
    const result: DateRange = {
      start: this.asDate(start),
      end: this.asDate(end),
    };
    result.daysCount = this.getWorkdays(result.start!, result.end!).length;
    return result;
  }




  /**
   * Returns earliest workday counted from startDate parameter. If startDate is workday, it is returned.
   * @param startDate start date, where to start searching a work day
   * @returns Workday in simple ISO format YYYY-MM-DD
   */
  public static getWorkday(startDate: DatelyObject): string | undefined {

    let newDayJs = Dates.getDayJs(Dates.asDate(startDate));
    if (!newDayJs) {
      return undefined;
    }
    let maxTurns = 365;
    while (maxTurns>0) {

      if (Dates.isWorkday(newDayJs)) {
        return Dates.asDate(newDayJs);
      }
      maxTurns -=1;
      if (maxTurns == 0) {
        return undefined;
      }
      newDayJs = newDayJs.add(1, "day");
    }
    return undefined;
  }

   /**
    * Gets the workdays between two dates including both days.
    * Returns empty array if there is no start or if end is before start.
    * Returns 1 day if there is no end: we assume that the user selected just one day.
    *
    * @param start A dately value as the start date (a string, JS Date object, DayJs etc.).
    * @param end Corresponding end date.
    * @param holidayGroup The holiday group for defining which days not to count as working day. The default group is Holiday.
    *
    * @returns The work days between the two dates (inclusive).
    */
   public static getWorkdays(start: DatelyObject, end: DatelyObject, holidayGroup: HolidayGroup = HolidayGroup.Holiday): string[] {
    let startDate = this.asDate(start);
    const endDate = this.asDate(end) || startDate;
    if (!startDate) {
      return [];
    }
    const result: string[] = [];
    while (startDate! <= endDate!) {
      if (this.isWorkday(startDate!, holidayGroup)) {
        result.push(startDate!);
      }
      startDate = this.asDate(this.getDayJs(startDate).add(1, "day"));
    }
    return result;
  }

  /**
   * Gets the first workday between start and end dates.
   *
   * @param start A dately value as the start date (a string, JS Date object, DayJs etc.).
   * @param end Corresponding end date.
   *
   * @returns The first work day in a range of to dates (or undefined if none found).
   */
  public static getFirstWorkDay(start: DatelyObject, end: DatelyObject): string | undefined {
    const startDate = this.asJSDate(start);
    if (!startDate) return;

    const endDate = this.asJSDate(end) || startDate;

    for (let current = startDate; current <= endDate; current = this.getDayJs(current).add(1, "day").toDate()) {
      if (this.isWorkday(current)) return this.asDate(current);
    }
    return;
  }

  /**
   * Gets the vacation days (6 day week Mon-Sat) between two dates including both days.
   * Returns empty array if there is no start or if end is before start.
   * Returns 1 day if there is no end: we assume that the user selected just one day.
   *
   * @param start A dately value as the start date (a string, JS Date object, DayJs etc.).
   * @param end Corresponding end date.
   * @param holidayGroup The holiday group for defining which days not to count as working day. The default group is Holiday.
   *
   * @returns The work days between the two dates (inclusive).
   */
  public static getVacationDays(start: DatelyObject, end: DatelyObject, holidayGroup: HolidayGroup = HolidayGroup.Holiday): string[] {
    let startDate = this.asDate(start);
    const endDate = this.asDate(end) || startDate;
    if (!startDate) {
      return [];
    }
    const result: string[] = [];
    while (startDate && endDate && startDate <= endDate) {
      if (this.isVacationday(startDate, holidayGroup)) {
        result.push(startDate);
      }
      startDate = this.asDate(this.getDayJs(startDate).add(1, "day"));
    }
    return result;
  }

  /**
   * Gets a formatted range of two dates.
   *
   * @param startDate A dately value as the start date (a string, JS Date object, DayJs etc.).
   * @param endDate Corresponding end date.
   *
   * @returns A date range string with possible formats:
   * "12. - 25.11.2019", "12.11. - 02.12.2019", "12.11.2019 - 12.01.2020", "12.01.2020 -" or "- 12.01.2020".
   */
  public static getFormattedRange(startDate: DatelyObject, endDate: DatelyObject): string {
    const formatFull = "DD.MM.YYYY";
    const start = this.getDayJs(startDate);
    const end = this.getDayJs(endDate);
    if (!start && !end) {
      return "-";
    }
    if (!start && end) {
      return " - " + end.format(formatFull);
    }
    if (start && !end) {
      return start.format(formatFull) + " - ";
    }
    if (start.isSame(end)) {
      return start.format(formatFull);
    }
    const formatMonthDay = "DD.MM.";
    const formatDay = "DD.";
    const sameYear = start.year() === end.year();
    const sameMonth = start.month() === end.month();
    return start.format(sameYear ? (sameMonth ? formatDay : formatMonthDay) : formatFull) + " - " + end.format(formatFull);
  }

  /**
   * Output a formatted date with default date format (may later be localized).
   * @param date A dately value (a string, JS Date object, DayJs etc.) that is formatted.
   * @returns A Simple date string e.g. "12.11.2019", if the value is empty or null returns "-".
   */
  public static getFormattedDate(date: DatelyObject | null | undefined): string {
    return this.format(date!, "DD.MM.YYYY", "-")!;
  }

  /**
   * Formats a dately object using DayJs formatting:
   * Default is "DD.MM.YYYY"
   * For all format string options, see https://day.js.org/docs/en/display/format
   */
  public static format(date: DatelyObject, format = "DD.MM.YYYY", nullValue: string | undefined = undefined): string | undefined {
    const dayJs = this.getDayJs(date);
    return dayJs ? dayJs.format(format) : nullValue;
  }

  /**
   * Gets the day of month as 1-based number: 1-31.
   *
   * @param datelyObject - Object that represents date: A string, JS Date object, DayJs etc.
   * Special string "today" can be used for fetching today's date.
   *
   * @returns Day of month between 1-31 or undefined if the object is not a dately object.
   */
  public static getDay(datelyObject: DatelyObject): number | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? dayJs.date() : undefined;
  }

  /**
   * Sets the day of month as 1-based number: 1-31.
   *
   * @param datelyObject - Object that represents date: A string, JS Date object, DayJs etc.
   * Special string "today" can be used for fetching today's date.
   * @param dayOfMonth - day of the month to be set (1-31 depending on month, will bubble to next if beyond endof month)
   * @returns Day of month between 1-31 or undefined if the object is not a dately object.
   */
  public static setDay(datelyObject: DatelyObject, dayOfMonth: number): string | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? this.asDate(dayJs.date(dayOfMonth)) : undefined;
  }


  /**
   * Gets the day of week as ISO 1-based number: 1-7.
   *
   * @param datelyObject - Object that represents date: A string, JS Date object, DayJs etc.
   * Special string "today" can be used for fetching today's date.
   *
   * @returns Day of week
   */
    public static getWeekday(datelyObject: DatelyObject): number | undefined {
      const dayJs = this.getDayJs(datelyObject);
      return dayJs ? dayJs.isoWeekday() : undefined;
    }


  /**
   * Gets isoWeek-number for provided date
   * @param date Base date for week number
   * @returns week number
   */
  public static getWeekNumber(date: DatelyObject): number | undefined {
    const dayJs = this.getDayJs(date);
    return dayJs ? dayJs.isoWeek(): undefined;
  }

  /**
   * Gets number of day in month in given datelyObject.
   * @param date Base date for getting the month
   * @returns numbber of days in the month of the given base date.
   */
  public static getDaysInMonth(date: DatelyObject): number | undefined {
    const dayJs = this.getDayJs(date);
    return dayJs ? dayJs.daysInMonth(): undefined;
  }


  /**
   * Gets the month as 1-based number: 1-12.
   *
   * @param datelyObject - Object that represents month: A string, JS Date object, DayJs etc.
   * Special string "today" can be used for fetching today's date.
   *
   * @returns Month between 1-12 or undefined if the object is not a dately object.
   */
  public static getMonth(datelyObject: DatelyObject): number | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? dayJs.month() + 1 : undefined;
  }

  /**
   * Gets the year of a date or today.
   *
   * @param datelyObject - Object that represents month: A string, JS Date object, DayJs etc.
   * Special string "today" can be used for fetching today's date.
   *
   * @returns Year, e.g. 2019 or null if the object is not a dately object.
   */
  public static getYear(datelyObject: DatelyObject): number | undefined {
    const dayJs = this.getDayJs(datelyObject);
    return dayJs ? dayJs.year() : undefined;
  }

  /**
   * Returns true if the given dately object is a holiday.
   * Please note that the weekend days are not by default holidays
   *
   * @param datelyObject - Object that should be converted to date: A string, JS Date object, DayJs etc.
   * @param holidayGroup - The holiday group for defining which days to count as holiday day. The default group is Holiday.
   *
   * @returns True, if the given dately object is a holiday.
   */
  public static isHoliday(datelyObject: DatelyObject,  holidayGroup: HolidayGroup = HolidayGroup.Holiday ): boolean {
      const djs = this.getDayJs(datelyObject);
      if (!djs) {
        return false;
      }
      if (!holidayGroup) {
        return false;
      }
      const holidays = Object.values(this.getYearlyHolidays(djs) ?? {}) as HolidayDate[];
      return !!holidays.find((x) => x.date == this.asDate(djs) && x.holidayGroups &&x.holidayGroups.indexOf(holidayGroup) >= 0);
  }

  /**
   * Returns true if the given dately object is a working day (5 day week).
   * The day must not be Saturday or Sunday or not any day in the given holiday categoria.
   *
   * @param datelyObject - Object that should be converted to date: A string, JS Date object, DayJs etc.
   * @param holidayGroup - The holiday group for defining which days to count as holiday day. The default group is Holiday.
   *
   * @returns True, if the given dately object is a working day.
   */
  public static isWorkday(datelyObject: DatelyObject, holidayGroup: HolidayGroup = HolidayGroup.Holiday): boolean {

    const djs = this.getDayJs(datelyObject);
    if (!djs) {
      return false;
    }

    if (djs.isoWeekday() === 6 || // Saturday
        djs.isoWeekday() === 7) { // Sunday
      return false;
    }

    if (!holidayGroup) {
      return true;
    }

    return !this.isHoliday(djs, holidayGroup);
  }

  /**
   * Returns true if the given dately object is a vacation day (6 day week).
   * The day must not be Sunday or not any day in the given holiday categoria.
   *
   * @param datelyObject - Object that should be converted to date: A string, JS Date object, DayJs etc.
   * @param holidayGroup - The holiday group for defining which days to count as holiday day. The default group is Holiday.
   *
   * @returns True, if the given dately object is a working day.
   */
  public static isVacationday(datelyObject: DatelyObject, holidayGroup: HolidayGroup = HolidayGroup.Holiday): boolean {

    const djs = this.getDayJs(datelyObject);
    if (!djs) {
      return false;
    }

    if ( djs.isoWeekday() === 7) { // Sunday
      return false;
    }

    if (!holidayGroup) {
      return true;
    }

    return !this.isHoliday(djs, holidayGroup);
  }

/**
 * Adds given amount of working days to given day.
 *
 * @param datelyObject - Object that should be converted to date: A string, JS Date object, DatyJs etc.
 * @param days - Working days to add (positive or negative number).
 * If zero or not set, returns datelyObject or next workday if datelyObject is not a workday.
 * @param holidayGroup - The holiday group for defining which days to count as holiday day. The default group is Holiday.
 *
 * @returns New working day with amount of given working days ahead.
 */
  public static addWorkdays(datelyObject: DatelyObject, days: number, holidayGroup: HolidayGroup = HolidayGroup.Holiday): string | undefined {
    let newDayJs = this.getDayJs(datelyObject);
    if (!newDayJs) {
      return undefined;
    }

    if (!days) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (this.isWorkday(newDayJs, holidayGroup)) {
          return this.asDate(newDayJs);
        }
        newDayJs = newDayJs.add(1, "d");
      }
    }
    let count = 0;
    const step = days > 0 ? 1 : -1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      newDayJs = newDayJs.add(step, "d");
      if (this.isWorkday(newDayJs, holidayGroup)) {
        count += step;
        if (count === days) {
          return this.asDate(newDayJs);
        }
      }
    }
  }

  /**
   * Adds (or substracts) days, months or years to a dately object.
   * @param datelyObject Base date for the addition.
   * @param amount Number of days/months/years to add
   * @param unit Unit for the addition: day,month,year
   */
  public static add(datelyObject: DatelyObject, amount: number, unit: "day" | "week" | "month" | "quarter" | "year"): string | undefined {
    const newDayJs = this.getDayJs(datelyObject);
    if (!newDayJs) {
      return undefined;
    }
    switch (unit) {
      case "day":
        return this.asDate(newDayJs.add(amount, "d"));
      case "week":
        return this.asDate(newDayJs.add(amount, "w"));
      case "month":
        return this.asDate(newDayJs.add(amount, "M"));
      case "quarter":
        return this.asDate(newDayJs.add(amount, "Q"));
      case "year":
        return this.asDate(newDayJs.add(amount, "y"));
      default:
        throw new Error(`Unit "${unit}" is not supported.`);
    }
  }

  /**
   * Changes a date to start/end of week/month/quarter/year
   * @param datelyObject Base date that is converted.
   * @param type Type of change: start-week, start-month, start-quarter, start-year, end-week, end-month, end-quarter, end-year
   */
  public static startEnd(datelyObject: DatelyObject, type: "start-week" | "start-month" | "start-quarter" | "start-year" | "end-week" | "end-month" | "end-quarter" | "end-year"): string | undefined {
    const newDayJs = this.getDayJs(datelyObject);
    if (!newDayJs) {
      return undefined;
    }


    switch (type) {
      case "start-week":
        return this.asDate(newDayJs.startOf("isoWeek"));
      case "start-month":
        return this.asDate(newDayJs.startOf("month"));
      case "start-quarter":
        return this.asDate(newDayJs.startOf("quarter"));
      case "start-year":
        return this.asDate(newDayJs.startOf("year"));
      case "end-week":
        return this.asDate(newDayJs.endOf("isoWeek"));
      case "end-month":
        return this.asDate(newDayJs.endOf("month"));
      case "end-quarter":
        return this.asDate(newDayJs.endOf("quarter"));
      case "end-year":
        return this.asDate(newDayJs.endOf("year"));
      default:
        throw new Error(`Start/end type "${type}" is not supported.`);
    }
  }


  /**
   * Parses the given date range string to a DateRange. Parsing is implemented for optimized date
   * range inputs. Optimized date range means that dates can be given in a shorter/truncated form,
   * for improved user efficiency, by omitting some components from the date strings:
   *
   * - For the end date the day is the only mandatory component, if month and/or year are omitted
   * they default to current month and year respectively
   * - For the start date all components are optional, any omitted components default to the
   * respective components from the end date.
   *
   * For example the result of parsing a user inputted optimized date range string "1 - 20" would be
   * { start: 2021-04-01, end: 2021-04-20, }.
   *
   * @param dateRange The optimized date range string to be parsed. The dates in the date range
   * should be in finnish format (dd.mm.yyyy or without leading zeros d.m.yyyy). Start and end dates
   * should be separated by a hyphen (-). Examples of valid date range strings could be e.g.
   * "1.4 - 1.5.2021" or "1" which would be parsed as { start: 2021-04-01, end: 2021-05-01 } and
   * { start: 2021-04-01, end: 2021-05-01 } respectively (at the time of writing this documentation).
   * @returns The parsed DateRange.
   */
  public static parseOptimizedDateRange(dateRange: string): DateRange | undefined {
    if (!this.validateDaterangeInput(dateRange)){
      return undefined;
    }
    const dr = this.parseDateRange(dateRange);
    if (!dr){
      return undefined;
    }
    if (!this.validateDaterangeOrder(dr)){
      return undefined;
    }
    return dr;
  }

  /**
   * Parses a start date from an optimized date range string. Uses the end date as the default if
   * start date or some of its components are not given.
   * @param dateRange - String to be parsed as start of DateRange
   * @returns string
   */
  private static parseStartDateFrom(dateRange: string): string | undefined {
    if (dateRange.indexOf("-") < 0) {
      return this.parseEndDateFrom(dateRange);
    }
    const fromDate = dateRange.substring(0, dateRange.indexOf("-")).trim();
    return this.parseOptimizedDate(fromDate, this.parseEndDateFrom(dateRange));
  }

  /**
   * Parses an end date from an optimized date range string. Uses the current date as the default if
   * month and/or year are not given.
   * @param dateRange - String to be parsed as end of DateRange
   * @returns string
   */
  private static parseEndDateFrom(dateRange: string): string | undefined {
    const toDate = dateRange.substring(dateRange.indexOf("-") + 1).trim();
    return this.parseOptimizedDate(toDate);
  }

  /**
   * Parses an optimized date string. The date string should be in finnish format (dd.mm.yyyy with
   * or without leading zeros). Month and/or year component can be omitted from the date string
   * (optimized date string...) in which case they are taken from the default date. If default date
   * is not given the current date is used
   * @param date - String to be parsed as DateRange
   * @param defaultDate default date, fallbacks to today
   * @returns string
   */
  private static parseOptimizedDate(date: string, defaultDate: string = this.getToday()!): string | undefined {
    const dateComponents = date.split(".");
    dateComponents[0] = this.parseDay(dateComponents[0]);
    dateComponents[1] = this.parseMonth(dateComponents[1], defaultDate);
    dateComponents[2] = this.parseYear(dateComponents[2], defaultDate);
    const joinedDate = dateComponents.reverse().join("-");
    if (this.isValidDateTime(joinedDate, true, "YYYY-MM-DD")){
      return joinedDate;
    }
    return undefined;
  }

  private static parseDay(day: string): string {
    if (!day || day.length > 2) {
      return "";
    }
    return day.length === 1 ? "0" + day : day;
  }

  /**
   * Parses an optimized month string. If the month is not given it is taken from the default date.
   */
  private static parseMonth(month: string, defaultDate: string): string {
    if (month?.length > 2) {
      return "";
    }
    if (!month) {
      month = this.getMonth(defaultDate)?.toString() ?? "";
    }
    return month.length === 1 ? "0" + month : month;


  }

  /**
   * Parses an optimized year string. Defaults to the 3rd millenium, if the given year doesn't
   * contain the millenia e.g. "21" --> "2021". If the year is not given at all, it is taken from
   * the default date.
   */
  private static parseYear(year: string, defaultDate: string): string {
    if (year?.length > 4) {
      return "";
    }
    if (!year) {
      return this.getYear(defaultDate)?.toString() ?? "";

    }
    return (parseInt(year)%2000 + 2000).toString();
  }


  /**
   * Parses DateRange from a string
   * @param dateRange string to be parsed as date range
   * @returns DateRange
   */
  private static parseDateRange(dateRange: string): DateRange | undefined {
    const dr =  { start: this.parseStartDateFrom(dateRange), end: this.parseEndDateFrom(dateRange) };
    return dr;
  }




  /**
   * Validates that the given date range string only contains allowed character (numbers, periods,
   * dashes, spaces).
   */
  private static validateDaterangeInput(dateRange: string): boolean {
    if (dateRange.match(/^[0-9 -.]*$/) == null) {
      return false;
    }
    return true;
  }



  /**
   * Validates a date range by checking that the start date is not after the end date.
   * This method does not validate start and end dates strictly, so 30.2.2023 will be translated internally dy Day.js to
   * 2.3.2023.
   * @param dateRange - date range to be validated
   */
  private static validateDaterangeOrder(dateRange: DateRange): boolean | undefined {
    const sdjs = this.getDayJs(dateRange.start);
    if (!sdjs){
      return undefined;
    }
    if (!sdjs.isValid()){
      return undefined;
    }
    const edjs = this.getDayJs(dateRange.end);
    if (!edjs){
      return undefined;
    }
    if (!edjs.isValid()){
      return undefined;
    }

    if (sdjs.isAfter(edjs)) {
      return false;
    }
    return true;
  }

  /**
   * Gets holidays for the given year.
   *
   * @param forYear - Dately object (JavaScript date, ISO string formatted date, or DayJs-object) for which the holidays are fetched.
   *
   * @returns The holidays for the given year.
   */
   public static getYearlyHolidays(forYear: DatelyObject): Holidays | undefined | null {
    const year = Dates.getYear(forYear);
    const yearlyHolidays = yearlyHolidaysYears.find((x) => x.year === year);
    if (!yearlyHolidays) {
      throw new Error(`Year ${year} not supported.`);
    }
    return yearlyHolidays.holidays;
  }

  /**
   * Checks if date1 is after date2
   * @param date1 comparable date1
   * @param date2 comparable date1
   * @param granularity granularity for isAfter, default is date
   * @returns true if date1 is after date2
   */
  public static isAfter(date1: DatelyObject, date2: DatelyObject, granularity = "date"): boolean {
    const date1AsDayJs = this.getDayJs(date1);
    const date2AsDayJs = this.getDayJs(date2);

    if (date1AsDayJs && date2AsDayJs ) {
      return date1AsDayJs.isAfter(this.getDayJs(date2AsDayJs), granularity);
    }
    return false;
  }

  /**
   * Checks if date1 is before date2
   * @param date1 comparable date1
   * @param date2 comparable date1
   * @param granularity granularity for isBefore, default is date
   * @returns true if date1 is before date2
   */
  public static isBefore(date1: DatelyObject, date2: DatelyObject, granularity = "date"): boolean {
    const date1AsDayJs = this.getDayJs(date1);
    const date2AsDayJs = this.getDayJs(date2);

    if (date1AsDayJs && date2AsDayJs ) {
      return date1AsDayJs.isBefore(this.getDayJs(date2AsDayJs), granularity);
    }
    return false;
  }

  /**
   * Checks if date1 is same or after date2
   * @param date1 comparable date1
   * @param date2 comparable date1
   * @param granularity granularity for isAfter, default is date
   * @returns true if date1 is is same or after date2
   */
  public static isSameOrAfter(date1: DatelyObject, date2: DatelyObject, granularity = "date"): boolean {
    const date1AsDayJs = this.getDayJs(date1);
    const date2AsDayJs = this.getDayJs(date2);
    if (date1AsDayJs && date2AsDayJs ) {
      return date1AsDayJs.isSameOrAfter(this.getDayJs(date2AsDayJs), granularity);
    }
    return false;
  }

  /**
   * Checks if date1 is same or before date2
   * @param date1 comparable date1
   * @param date2 comparable date1
   * @param granularity granularity for isAfter, default is date
   * @returns true if date1 is same or before date2
   */
  public static isSameOrBefore(date1: DatelyObject, date2: DatelyObject, granularity = "date"): boolean {
    const date1AsDayJs = this.getDayJs(date1);
    const date2AsDayJs = this.getDayJs(date2);
    if (date1AsDayJs && date2AsDayJs ) {
      return date1AsDayJs.isSameOrBefore(this.getDayJs(date2AsDayJs), granularity);
    }
    return false;
  }





}
